'use client'

import dynamic from 'next/dynamic'
import { useState } from 'react'

import { Drawer } from '@/components/Drawer'
import { EventsFormSkeleton } from '@/components/EventsForm/EventsFormSkeleton'
import { usePrefetchOnFirstInteraction } from '@/hooks/usePrefetchOnFirstInteraction'
import { useEventsFormProviderContext } from '@/providers/EventsFormProvider'

const loadEventsForm = () => import('@/components/EventsForm')

const EventsFormInner = dynamic(loadEventsForm, {
  ssr: false,
  loading: () => <EventsFormSkeleton />,
})

/**
 * Defers the event submission form (react-hook-form, Lexical editor, maps
 * field, etc.) out of the shared layout bundle. The chunk is prefetched on
 * first interaction. The drawer shell mounts here and animates in exactly
 * once per open; while the form chunk downloads, the drawer shows the
 * skeleton, then the real form swaps in place inside it.
 */
const EventsFormLazy = () => {
  const { isCreateForm, editEventId, closeForm } = useEventsFormProviderContext()
  const open = isCreateForm || (editEventId?.length ?? 0) > 0

  // Stay mounted after the first open so the close animation can play and
  // re-opens are instant. Each open bumps the session key so the form
  // remounts with fresh state (replaces the old reset-on-close logic).
  const [session, setSession] = useState(0)
  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setSession((s) => s + 1)
  }

  usePrefetchOnFirstInteraction(loadEventsForm)

  if (session === 0) return null

  return (
    <Drawer
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) closeForm()
      }}
      ariaTitle={editEventId ? 'Edit your Event' : 'Submit an Event'}
    >
      <EventsFormInner key={session} />
    </Drawer>
  )
}

export default EventsFormLazy
